import { useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  usePostPlaylistMutation,
  usePostPriorityTaskMutation,
  usePutPlaylistMutation,
  usePutPriorityTaskMutation,
  useGetPlaylistsQuery,
  useGetTaskTypesQuery,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.api';
import {
  closeDialog,
  closePlaylistDrawer,
  openNewTaskDialog,
  openPlaylistViewStep,
  selectNuggetToAdd,
  selectPrioritiesDialog,
  setNuggetToAdd,
  selectSecondaryDialog,
  closeSecondaryDialog,
  setSelectedPlaylistForNewTaskDialog,
  setSelectedPlaylistForEditTaskDialog,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import { PriorityPlaylistPost } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { Box, Snackbar, SxProps, Theme } from '@mui/material';

import { PlaylistDialog } from '../atoms/PlaylistDialog';
import { TaskDialog } from '../atoms/TaskDialog';

const FORM_STRINGS = appStrings.modules.priorities.taskDialog;

export const PrioritiesForm = () => {
  const dispatch = useAppDispatch();
  const dialog = useAppSelector(selectPrioritiesDialog);
  const secondaryDialog = useAppSelector(selectSecondaryDialog);
  const { data: playlists } = useGetPlaylistsQuery(undefined);
  const { data: taskTypes } = useGetTaskTypesQuery(undefined);
  const nuggetToAdd = useAppSelector(selectNuggetToAdd);

  const [createTask] = usePostPriorityTaskMutation();
  const [updateTask] = usePutPriorityTaskMutation();
  const [createPlaylist] = usePostPlaylistMutation();
  const [updatePlaylist] = usePutPlaylistMutation();

  // State for snackbar notification
  const [showUnassignedSnackbar, setShowUnassignedSnackbar] = useState(false);

  const handleClosePlaylist = (shouldClosePlaylistDrawer = true) => {
    dispatch(closeDialog());

    if (shouldClosePlaylistDrawer) {
      dispatch(closePlaylistDrawer());
    }
  };

  const handleCloseTaskDialog = (shouldClosePlaylistDrawer = true) => {
    dispatch(closeDialog());
    dispatch(setNuggetToAdd(null));

    if (shouldClosePlaylistDrawer) {
      dispatch(closePlaylistDrawer());
    }
  };

  if (!dialog && !secondaryDialog) {
    return (
      <Snackbar
        open={showUnassignedSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowUnassignedSnackbar(false)}
        message={FORM_STRINGS.unassignedTaskCreated}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        action={
          <Box onClick={() => setShowUnassignedSnackbar(false)} sx={snackbarContainerSx}>
            <AppIcon iconName="XClose" diameter="16px" color="Colors/Base/White" />
          </Box>
        }
      />
    );
  }

  const handlePlaylistCreate = async (playlist: PriorityPlaylistPost) => {
    const newPlaylist = await createPlaylist(playlist).unwrap();

    if (nuggetToAdd) {
      dispatch(openPlaylistViewStep(newPlaylist));
      dispatch(openNewTaskDialog({ playlist: newPlaylist, shouldAutoOpenPlaylistDrawer: false }));
    }
  };

  const closeSecondaryHandler = (shouldClosePlaylistDrawer = false) => {
    if (shouldClosePlaylistDrawer) {
      dispatch(closePlaylistDrawer());
    }

    dispatch(closeSecondaryDialog());
  };

  const handleSecondaryPlaylistCreate = async (playlist: PriorityPlaylistPost) => {
    const newPlaylist = await createPlaylist(playlist).unwrap();

    if (dialog?.type === 'NEW_TASK') {
      dispatch(setSelectedPlaylistForNewTaskDialog(newPlaylist));
    } else if (dialog?.type === 'EDIT_TASK') {
      dispatch(setSelectedPlaylistForEditTaskDialog(newPlaylist));
    }

    dispatch(closeSecondaryDialog());
  };

  const renderPrimary = () => {
    if (!dialog) return null;

    switch (dialog.type) {
      case 'NEW_TASK':
        return (
          <TaskDialog
            playlists={playlists || []}
            taskTypes={taskTypes || []}
            nuggetToAdd={nuggetToAdd}
            selectedPlaylist={dialog.playlist}
            preloadedTaskInfo={dialog.preloadedTaskInfo}
            handleClose={handleCloseTaskDialog}
            handleTaskCreate={async (task) => {
              const createdTask = await createTask(task).unwrap();

              if (!createdTask.playlist_id && nuggetToAdd) {
                setShowUnassignedSnackbar(true);
              }
            }}
          />
        );
      case 'EDIT_TASK':
        return (
          <TaskDialog
            playlists={playlists || []}
            taskTypes={taskTypes || []}
            nuggetToAdd={null}
            taskToEdit={dialog.task}
            selectedPlaylist={dialog.selectedPlaylist}
            handleClose={handleCloseTaskDialog}
            handleTaskCreate={async () => {
              // This should never be called in edit mode
            }}
            handleTaskUpdate={async (task) => {
              await updateTask({ ...task, id: dialog.task.id });
            }}
          />
        );
      case 'NEW_PLAYLIST':
        return (
          <PlaylistDialog
            handleClose={handleClosePlaylist}
            handlePlaylistCreate={handlePlaylistCreate}
            isPendingAddNewNugget={!!nuggetToAdd}
            preLoadedTitle={dialog.preLoadedTitle}
          />
        );
      case 'EDIT_PLAYLIST':
        return (
          <PlaylistDialog
            isPendingAddNewNugget={false}
            playlistToEdit={dialog.playlist}
            handleClose={handleClosePlaylist}
            handlePlaylistCreate={async () => {
              // This should never be called in edit mode
            }}
            handlePlaylistUpdate={async (playlist) => {
              await updatePlaylist({ ...playlist, id: dialog.playlist.id });
            }}
          />
        );
      default:
        return null;
    }
  };

  const renderSecondary = () => {
    if (!secondaryDialog) return null;

    switch (secondaryDialog.type) {
      case 'NEW_PLAYLIST':
        return (
          <PlaylistDialog
            handleClose={closeSecondaryHandler}
            handlePlaylistCreate={handleSecondaryPlaylistCreate}
            isPendingAddNewNugget={!!nuggetToAdd}
            preLoadedTitle={secondaryDialog.preLoadedTitle}
          />
        );
      case 'EDIT_PLAYLIST':
        return (
          <PlaylistDialog
            isPendingAddNewNugget={false}
            playlistToEdit={secondaryDialog.playlist}
            handleClose={closeSecondaryHandler}
            handlePlaylistCreate={async () => {
              // This should never be called in edit mode
            }}
            handlePlaylistUpdate={async (playlist) => {
              await updatePlaylist({ ...playlist, id: secondaryDialog.playlist.id });
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {renderPrimary()}
      {renderSecondary()}
    </>
  );
};

const snackbarContainerSx: SxProps<Theme> = {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
